// User preferences. Account-bound since the server-settings migration: every non-face key
// lives in the per-user settings blob (user-settings-sync, key "uiPreferences") and follows
// the user across devices; the six face-unlock keys stay in a device-local localStorage key
// because the lock screen needs them BEFORE anyone logs in and camera deviceIds don't
// travel. The public API is unchanged — getUserPreferences() still returns one merged,
// identity-stable object with all keys, and setUserPreferences() routes each key to its
// store. Logged out, reads serve defaults + the device face keys.
import {
  USER_SETTING_KEYS,
  getActiveSettingsUid,
  readUserSetting,
  subscribeUserSettingsActivation,
  writeUserSetting,
} from "@/lib/stores/user-settings-sync";

const DEVICE_PREFERENCES_STORAGE_KEY = "healo-device-preferences";
const USER_PREFERENCES_CHANGE_EVENT = "healo-user-preferences-change";

// The device-local subset: readable pre-auth (lock-screen face unlock) and never uploaded.
const FACE_PREFERENCE_KEYS = Object.freeze([
  "faceUnlockEnabled",
  "faceAutoSwitchEnabled",
  "faceLockOnStrangerEnabled",
  "faceStrangerGraceSeconds",
  "faceOwnerAbsentSeconds",
  "faceCameraDeviceId",
]);

// Hour-row height is a free-form percentage (presets are just quick-picks in the
// UI). Clamped to a sane range so a stray value can't make rows unusable.
const DEFAULT_CALENDAR_ZOOM_PERCENT = 100;
const MIN_CALENDAR_ZOOM_PERCENT = 50;
const MAX_CALENDAR_ZOOM_PERCENT = 1200;

// Month view has no time axis, so its zoom scales the day-cell heights instead of
// hour-row heights. It is a separate value so the month "cell height" and the day/week
// "hour row height" can be remembered independently. Capped lower than the time-grid
// zoom — very tall month cells get unwieldy fast.
const DEFAULT_CALENDAR_MONTH_ZOOM_PERCENT = 100;
const MIN_CALENDAR_MONTH_ZOOM_PERCENT = 50;
const MAX_CALENDAR_MONTH_ZOOM_PERCENT = 800;

// Horizontal ("width") zoom for the day & week time grids — scales the column widths
// (alt+wheel, or the horizontal half of a plain ctrl/cmd+wheel). Separate from the vertical
// hour-row zoom so the two axes are remembered independently.
const DEFAULT_CALENDAR_WIDTH_ZOOM_PERCENT = 100;
const MIN_CALENDAR_WIDTH_ZOOM_PERCENT = 50;
const MAX_CALENDAR_WIDTH_ZOOM_PERCENT = 800;


// Open-slot pill opacity (%). 100 = fully opaque; lower = fainter so booked appointments
// stand out. Clamped so slots can't become fully invisible.
const DEFAULT_CALENDAR_SLOT_OPACITY_PERCENT = 50;
const MIN_CALENDAR_SLOT_OPACITY_PERCENT = 10;
const MAX_CALENDAR_SLOT_OPACITY_PERCENT = 100;

const VALID_CALENDAR_VIEWS = ["day", "week", "month"];
const DEFAULT_CALENDAR_VIEW = "month";

const VALID_CALENDAR_COLUMN_WIDTH_MODES = ["dynamic", "comfortable", "spacious", "custom"];
const DEFAULT_CALENDAR_COLUMN_WIDTH_MODE = "dynamic";
// The pixel width used when column-width mode is "custom".
const DEFAULT_CALENDAR_COLUMN_WIDTH_CUSTOM_PX = 280;
const MIN_CALENDAR_COLUMN_WIDTH_PX = 160;
const MAX_CALENDAR_COLUMN_WIDTH_PX = 640;

const VALID_CALENDAR_DAY_VIEW_GROUP_BY = ["tenant", "doctor"];
const DEFAULT_CALENDAR_DAY_VIEW_GROUP_BY = "tenant";

// Day/week time-grid window: the visible span of the day. Whole hours only; the
// end is kept strictly after the start (see normalizeUserPreferences).
const DEFAULT_CALENDAR_DAY_START_HOUR = 0;
const DEFAULT_CALENDAR_DAY_END_HOUR = 24;

// "Min interval": spacing (minutes) of the time-grid tick rows. Restricted to
// divisors of 60 so tick lines and hour labels always align.
const VALID_CALENDAR_TICK_MINUTES = [5, 10, 15, 30, 60];
const DEFAULT_CALENDAR_TICK_MINUTES = 30;

// Day-view "session rail": a thin background indicator on each column showing
// which doctor/clinic runs a session there. Width is a small px range; color mode
// is the per-entity accent hue or a calm neutral gray.
const DEFAULT_CALENDAR_SHOW_SESSION_RAIL = true;
const DEFAULT_CALENDAR_RAIL_WIDTH_PX = 26;
const MIN_CALENDAR_RAIL_WIDTH_PX = 14;
const MAX_CALENDAR_RAIL_WIDTH_PX = 40;
const VALID_CALENDAR_RAIL_COLOR_MODES = ["accent", "neutral"];
const DEFAULT_CALENDAR_RAIL_COLOR_MODE = "accent";
// Left gutter before the rail — nudges the rail (and the cards after it) right so
// consecutive day/entity columns read with a little breathing room.
const DEFAULT_CALENDAR_RAIL_MARGIN_PX = 0;
const MIN_CALENDAR_RAIL_MARGIN_PX = 0;
const MAX_CALENDAR_RAIL_MARGIN_PX = 8;

// Work Plan's right-hand roster rail ("Support staff"), drag-resizable. Long staff names
// and the role/AM-PM tags need more room on wide screens than the original fixed 236px.
const DEFAULT_WORK_PLAN_RAIL_WIDTH_PX = 236;
const MIN_WORK_PLAN_RAIL_WIDTH_PX = 200;
const MAX_WORK_PLAN_RAIL_WIDTH_PX = 520;

const VALID_THREAD_LIST_VIEWS = ["standard", "groups"];
const DEFAULT_THREAD_LIST_VIEW = "standard";

// Messages list lens: "all" (chronological, the calm default), "needs" (the triage piles —
// Waiting for you + Unanswered bot replies), "pinned" (the user's local message bookmarks),
// "waGroups" (bridged WhatsApp group chats — a separate server scope, never mixed into "all").
// DISABLED (for now): "mine" (assigned to me) — thread assignment is hidden; persisted
// "mine" preferences fall back to the default lens.
const VALID_THREAD_LENSES = ["all", "needs", "pinned", "waGroups" /* , "mine" */];
const DEFAULT_THREAD_LENS = "all";

// Messages view mode: the thread list ("list") or the triage board ("board").
const VALID_MESSAGES_VIEW_MODES = ["list", "board"];
const DEFAULT_MESSAGES_VIEW_MODE = "list";

// Tasks view mode: the team queue ("list") or the swim-lane task board ("board").
const VALID_TASKS_VIEW_MODES = ["list", "board"];
const DEFAULT_TASKS_VIEW_MODE = "list";

// Merge all of a patient's WhatsApp threads (one per clinic/tenant number) into one
// chronological conversation with per-clinic marking and a reply-identity picker. Can be
// toggled in Preferences. See message-cluster-list.jsx (segments/rail)
// and message-composer.jsx (reply-from picker).
const DEFAULT_MERGED_WHATSAPP_VIEW = false;

// Merged WhatsApp GROUP rows: one row per WhatsApp group instead of one per clinic gateway.
// Unrelated to mergedWhatsAppView above — that folds ONE PATIENT's threads across clinic
// numbers and interleaves their histories; this folds the per-gateway copies of ONE GROUP,
// which all carry the same messages, so the merged row shows a single history plus a
// reply-from picker. Deliberately a separate flag: reusing the patient one would silently
// switch group merging on for everyone who already enabled that.
const DEFAULT_MERGED_WA_GROUPS_VIEW = false;

// RETIRED: the sidebar's mono/colored toggle. The nav now has one zoned style in both widths
// (see sidebar-icon-colors.js), so nothing reads this any more — the key is kept only so the
// stored value on existing profiles round-trips through normalize instead of being dropped.
const VALID_SIDEBAR_COLOR_MODES = ["mono", "colored"];
const DEFAULT_SIDEBAR_COLOR_MODE = "mono";

// Whether the left app sidebar is collapsed to its narrow icon rail.
const DEFAULT_SIDEBAR_COLLAPSED = false;

// Face unlock (device-local; see src/lib/face/). The master switch defaults OFF — the camera
// must never open because someone installed an update.
//
// The two behaviours underneath are deliberately separate switches, because they are not the
// same trade. Locking on a stranger only ever *removes* access, so a false positive costs a PIN
// tap — it is safe to leave on. Auto-switch *grants* access from a face match, so a false
// positive hands one clinician another's session; it is worth being able to keep the safety
// half without the convenience half.
const DEFAULT_FACE_UNLOCK_ENABLED = false;
const DEFAULT_FACE_AUTO_SWITCH_ENABLED = true;
const DEFAULT_FACE_LOCK_ON_STRANGER_ENABLED = true;
// Seconds a stranger sees the "this isn't your session" message before the session locks.
// Long enough to read and step away, short enough to matter.
const DEFAULT_FACE_STRANGER_GRACE_SECONDS = 10;
const MIN_FACE_STRANGER_GRACE_SECONDS = 3;
const MAX_FACE_STRANGER_GRACE_SECONDS = 60;

// How long the logged-in user must go unrecognized before an unfamiliar face at the monitor is
// treated as someone else rather than as a bad frame of them. See the stranger branch in
// face-auth-policy.js for why this exists at all: "stranger" only ever means "a face we
// couldn't match", and the face that most often fails to match is the owner's own (looking down,
// turning away, backlight).
//
// The floor is 10s rather than 0 because the failure it prevents is severe and silent: set it
// low and the app starts accusing people of not being themselves mid-task, which is how the
// whole feature ends up switched off. The ceiling is 10 minutes — past that the idle auto-lock
// (session_lock, default 10 min) is the thing actually protecting the machine anyway.
const DEFAULT_FACE_OWNER_ABSENT_SECONDS = 60;
const MIN_FACE_OWNER_ABSENT_SECONDS = 10;
const MAX_FACE_OWNER_ABSENT_SECONDS = 600;
// Which camera face unlock watches. "" = whichever the system hands us, which is right for the
// overwhelming majority (one built-in webcam); the setting exists for machines with an external
// camera pointed at the seat and a built-in one pointed at the ceiling. Deliberately NOT
// validated against a device list: enumerateDevices is async and hides deviceIds until camera
// permission is granted, so a stale id is resolved at open time (see openFaceCameraStream)
// rather than eagerly scrubbed here — unplugging a webcam must not silently erase the choice.
const DEFAULT_FACE_CAMERA_DEVICE_ID = "";

// Global timezone override (account-bound). "" = follow the machine's own zone — the default, and
// byte-for-byte the behaviour from before this setting existed. A non-empty value must be an IANA
// zone the runtime recognises (see normalizeAppTimeZone); when set, the whole app renders every
// instant in this zone instead of the system default. See src/lib/timezone.js for how it is applied.
const DEFAULT_APP_TIME_ZONE = "";

// NOTE: the "Unanswered bot replies" thresholds moved to the tenant-wide shared settings
// (GET/PUT /api/settings/messages, collection global_settings) — they are no longer device
// preferences. Stale messagesBotAck* keys in persisted localStorage payloads are ignored.

// NOTE: the Messages "threads per page" preference (messagesThreadsPageSize) was retired when
// the thread list became fully virtual — rows load in fixed chunks as the user scrolls. Stale
// keys in persisted localStorage payloads are ignored, same as the messagesBotAck* keys above.

const DEFAULT_USER_PREFERENCES = Object.freeze({
  calendarZoomPercent: DEFAULT_CALENDAR_ZOOM_PERCENT,
  calendarMonthZoomPercent: DEFAULT_CALENDAR_MONTH_ZOOM_PERCENT,
  calendarWidthZoomPercent: DEFAULT_CALENDAR_WIDTH_ZOOM_PERCENT,
  calendarSlotOpacityPercent: DEFAULT_CALENDAR_SLOT_OPACITY_PERCENT,
  calendarView: DEFAULT_CALENDAR_VIEW,
  calendarUse12HourFormat: false,
  calendarIncludeSlots: true,
  calendarIncludeNotAvailableSlots: false,
  calendarShowCancelled: false,
  // Present the main filter line's Clinics / Doctors facet as an inline row of
  // quick-toggle checkboxes (true) instead of the compact searchable dropdown
  // (false, the default). One switch per facet lives in the Display popover.
  calendarClinicsQuickFilter: false,
  calendarDoctorsQuickFilter: false,
  // The user's own order for the Clinics / Doctors filter chips. Empty = the
  // server's order. This drives the DAY-VIEW COLUMN ORDER too, since the columns
  // are a filtered view of the same option arrays. Ids that disappear are skipped
  // and entities the list never saw stay where the server put them (orderByIds).
  calendarClinicOrder: [],
  calendarDoctorOrder: [],
  calendarFilterBadgesExpanded: true,
  calendarColumnWidthMode: DEFAULT_CALENDAR_COLUMN_WIDTH_MODE,
  calendarColumnWidthCustomPx: DEFAULT_CALENDAR_COLUMN_WIDTH_CUSTOM_PX,
  calendarDayViewGroupBy: DEFAULT_CALENDAR_DAY_VIEW_GROUP_BY,
  calendarDaySplitEnabled: false,
  calendarDayStartHour: DEFAULT_CALENDAR_DAY_START_HOUR,
  calendarDayEndHour: DEFAULT_CALENDAR_DAY_END_HOUR,
  calendarTickMinutes: DEFAULT_CALENDAR_TICK_MINUTES,
  calendarShowSessionRail: DEFAULT_CALENDAR_SHOW_SESSION_RAIL,
  calendarRailWidthPx: DEFAULT_CALENDAR_RAIL_WIDTH_PX,
  calendarRailColorMode: DEFAULT_CALENDAR_RAIL_COLOR_MODE,
  calendarRailMarginPx: DEFAULT_CALENDAR_RAIL_MARGIN_PX,
  calendarHideSunday: false,
  workPlanRailWidthPx: DEFAULT_WORK_PLAN_RAIL_WIDTH_PX,
  // Work Plan clinics the user hid from the planner (rows, rail and session counts). Stored
  // as the HIDDEN ids, not the visible ones, so a clinic added later shows up by default
  // instead of silently staying out of a stale "visible" list.
  workPlanHiddenClinicIds: [],
  // The user's own clinic row order for the Work Plan planner. Empty = the server's order;
  // ids that leave the day are skipped and clinics the list never saw stay where the server
  // put them (see orderClinics).
  workPlanClinicOrder: [],
  // The Dashboard "Today" card's clinic. "" = none chosen — the card shows its pick-a-clinic
  // prompt; deliberately NEVER auto-defaulted for multi-clinic users (schedules must not be
  // mixed up), while a single-clinic user is auto-selected by the card.
  dashboardClinicId: "",
  // Lens model: the chronological "All" lens is the calm default, so the old always-on
  // locked-only filter default is off. messagesLensMigrationV1 marks the one-time reset of
  // the previously-persisted locked-only default (true on fresh installs so it never runs).
  messagesLensMigrationV1: true,
  messagesThreadFilterLockedOnly: false,
  messagesThreadFilterStaffHandlesOnly: false,
  messagesThreadFilterAwaitingReplyOnly: false,
  messagesThreadFilterWhatsappOnly: false,
  messagesThreadFilterAssignedToMe: false,
  messagesThreadFilterAssigneeId: "",
  messagesThreadLens: DEFAULT_THREAD_LENS,
  messagesViewMode: DEFAULT_MESSAGES_VIEW_MODE,
  tasksViewMode: DEFAULT_TASKS_VIEW_MODE,
  // Teammate ids the user pinned to always show as a lane on the task board (so idle people
  // are still drop targets for assignment). You + anyone with tasks always show regardless.
  tasksBoardVisibleAssigneeIds: [],
  // Internal Chats the user muted: no desktop notifications, excluded from the sidebar
  // Chats bubble (the per-row unread badge stays). Follows the user across devices.
  mutedChatThreadIds: [],
  messagesThreadListView: DEFAULT_THREAD_LIST_VIEW,
  mergedWhatsAppView: DEFAULT_MERGED_WHATSAPP_VIEW,
  mergedWaGroupsView: DEFAULT_MERGED_WA_GROUPS_VIEW,
  messagesThreadListCollapsedSections: [],
  messagesParticipantsCollapsedSections: [],
  // "Rewrite with AI" dialog: last-used persona id ("" = follow the backend default) and a
  // SPARSE map of only the explicitly-toggled switch ids → boolean, so persona/backend default
  // changes still apply to switches the user never touched.
  draftReplyAiPersona: "",
  draftReplyAiOptions: {},
  sidebarColorMode: DEFAULT_SIDEBAR_COLOR_MODE,
  sidebarCollapsed: DEFAULT_SIDEBAR_COLLAPSED,
  faceUnlockEnabled: DEFAULT_FACE_UNLOCK_ENABLED,
  faceAutoSwitchEnabled: DEFAULT_FACE_AUTO_SWITCH_ENABLED,
  faceLockOnStrangerEnabled: DEFAULT_FACE_LOCK_ON_STRANGER_ENABLED,
  faceStrangerGraceSeconds: DEFAULT_FACE_STRANGER_GRACE_SECONDS,
  faceOwnerAbsentSeconds: DEFAULT_FACE_OWNER_ABSENT_SECONDS,
  faceCameraDeviceId: DEFAULT_FACE_CAMERA_DEVICE_ID,
  appTimeZone: DEFAULT_APP_TIME_ZONE,
});

// The uid the cached merged object was built for (null = never built). Rebuilt on identity
// change/activation or a real set; otherwise getUserPreferences returns the SAME reference —
// useSyncExternalStore consumers rely on that identity stability.
let inMemoryPreferences = null;
let loadedForUid = null;
let activationSubscribed = false;

function canUseBrowserStorage() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function normalizeCalendarZoomPercent(value) {
  const num = Math.round(Number(value));
  if (!Number.isFinite(num) || num <= 0) {
    return DEFAULT_CALENDAR_ZOOM_PERCENT;
  }
  return Math.min(MAX_CALENDAR_ZOOM_PERCENT, Math.max(MIN_CALENDAR_ZOOM_PERCENT, num));
}

function normalizeCalendarMonthZoomPercent(value) {
  const num = Math.round(Number(value));
  if (!Number.isFinite(num) || num <= 0) {
    return DEFAULT_CALENDAR_MONTH_ZOOM_PERCENT;
  }
  return Math.min(MAX_CALENDAR_MONTH_ZOOM_PERCENT, Math.max(MIN_CALENDAR_MONTH_ZOOM_PERCENT, num));
}

function normalizeCalendarWidthZoomPercent(value) {
  const num = Math.round(Number(value));
  if (!Number.isFinite(num) || num <= 0) {
    return DEFAULT_CALENDAR_WIDTH_ZOOM_PERCENT;
  }
  return Math.min(MAX_CALENDAR_WIDTH_ZOOM_PERCENT, Math.max(MIN_CALENDAR_WIDTH_ZOOM_PERCENT, num));
}

function normalizeCalendarSlotOpacityPercent(value) {
  const num = Math.round(Number(value));
  if (!Number.isFinite(num) || num <= 0) {
    return DEFAULT_CALENDAR_SLOT_OPACITY_PERCENT;
  }
  return Math.min(MAX_CALENDAR_SLOT_OPACITY_PERCENT, Math.max(MIN_CALENDAR_SLOT_OPACITY_PERCENT, num));
}

function normalizeCalendarColumnWidthCustomPx(value) {
  const num = Math.round(Number(value));
  if (!Number.isFinite(num) || num <= 0) {
    return DEFAULT_CALENDAR_COLUMN_WIDTH_CUSTOM_PX;
  }
  return Math.min(MAX_CALENDAR_COLUMN_WIDTH_PX, Math.max(MIN_CALENDAR_COLUMN_WIDTH_PX, num));
}

function normalizeCalendarView(value) {
  const s = typeof value === "string" ? value.trim().toLowerCase() : "";
  return VALID_CALENDAR_VIEWS.includes(s) ? s : DEFAULT_CALENDAR_VIEW;
}

function normalizeCalendarUse12HourFormat(value) {
  return Boolean(value);
}

function normalizeCalendarIncludeSlots(value) {
  return value === undefined ? true : Boolean(value);
}

function normalizeCalendarIncludeNotAvailableSlots(value) {
  return Boolean(value);
}

function normalizeCalendarShowCancelled(value) {
  return value === undefined ? false : Boolean(value);
}

function normalizeCalendarQuickFilter(value) {
  // Default off (searchable dropdown) when unset; honour an explicit stored boolean.
  return value === undefined ? false : Boolean(value);
}

function normalizeCalendarFilterBadgesExpanded(value) {
  // Default on (saved-filter bar expanded) when unset; honour an explicit stored boolean.
  return value === undefined ? true : Boolean(value);
}

function normalizeCalendarColumnWidthMode(value) {
  const s = typeof value === "string" ? value.trim().toLowerCase() : "";
  return VALID_CALENDAR_COLUMN_WIDTH_MODES.includes(s) ? s : DEFAULT_CALENDAR_COLUMN_WIDTH_MODE;
}

function normalizeCalendarDayViewGroupBy(value) {
  const s = typeof value === "string" ? value.trim().toLowerCase() : "";
  return VALID_CALENDAR_DAY_VIEW_GROUP_BY.includes(s) ? s : DEFAULT_CALENDAR_DAY_VIEW_GROUP_BY;
}

// Day view: when true, a second day pane renders next to the selected day.
function normalizeCalendarDaySplitEnabled(value) {
  return Boolean(value);
}

function normalizeCalendarDayStartHour(value) {
  const num = Math.round(Number(value));
  if (!Number.isFinite(num)) {
    return DEFAULT_CALENDAR_DAY_START_HOUR;
  }
  return Math.min(23, Math.max(0, num));
}

function normalizeCalendarDayEndHour(value) {
  const num = Math.round(Number(value));
  if (!Number.isFinite(num)) {
    return DEFAULT_CALENDAR_DAY_END_HOUR;
  }
  return Math.min(24, Math.max(1, num));
}

function normalizeCalendarTickMinutes(value) {
  const num = Math.round(Number(value));
  return VALID_CALENDAR_TICK_MINUTES.includes(num) ? num : DEFAULT_CALENDAR_TICK_MINUTES;
}

function normalizeCalendarShowSessionRail(value) {
  return value === undefined ? DEFAULT_CALENDAR_SHOW_SESSION_RAIL : Boolean(value);
}

function normalizeCalendarRailWidthPx(value) {
  const num = Math.round(Number(value));
  if (!Number.isFinite(num)) {
    return DEFAULT_CALENDAR_RAIL_WIDTH_PX;
  }
  return Math.min(MAX_CALENDAR_RAIL_WIDTH_PX, Math.max(MIN_CALENDAR_RAIL_WIDTH_PX, num));
}

function normalizeWorkPlanRailWidthPx(value) {
  const num = Math.round(Number(value));
  if (!Number.isFinite(num)) {
    return DEFAULT_WORK_PLAN_RAIL_WIDTH_PX;
  }
  return Math.min(MAX_WORK_PLAN_RAIL_WIDTH_PX, Math.max(MIN_WORK_PLAN_RAIL_WIDTH_PX, num));
}

function normalizeCalendarRailColorMode(value) {
  const s = typeof value === "string" ? value.trim().toLowerCase() : "";
  return VALID_CALENDAR_RAIL_COLOR_MODES.includes(s) ? s : DEFAULT_CALENDAR_RAIL_COLOR_MODE;
}

function normalizeCalendarRailMarginPx(value) {
  const num = Math.round(Number(value));
  if (!Number.isFinite(num)) {
    return DEFAULT_CALENDAR_RAIL_MARGIN_PX;
  }
  return Math.min(MAX_CALENDAR_RAIL_MARGIN_PX, Math.max(MIN_CALENDAR_RAIL_MARGIN_PX, num));
}

// When true, calendar weekday grids hide the Sunday column.
function normalizeCalendarHideSunday(value) {
  return Boolean(value);
}

function normalizeMessagesThreadFilterLockedOnly(value) {
  return typeof value === "boolean" ? value : DEFAULT_USER_PREFERENCES.messagesThreadFilterLockedOnly;
}

function normalizeMessagesThreadFilterStaffHandlesOnly(value) {
  return typeof value === "boolean"
    ? value
    : DEFAULT_USER_PREFERENCES.messagesThreadFilterStaffHandlesOnly;
}

function normalizeMessagesThreadFilterAwaitingReplyOnly(value) {
  return typeof value === "boolean"
    ? value
    : DEFAULT_USER_PREFERENCES.messagesThreadFilterAwaitingReplyOnly;
}

function normalizeMessagesThreadFilterWhatsappOnly(value) {
  return typeof value === "boolean" ? value : DEFAULT_USER_PREFERENCES.messagesThreadFilterWhatsappOnly;
}

function normalizeMessagesThreadFilterAssignedToMe(value) {
  return typeof value === "boolean"
    ? value
    : DEFAULT_USER_PREFERENCES.messagesThreadFilterAssignedToMe;
}

function normalizeMessagesThreadFilterAssigneeId(value) {
  return typeof value === "string" ? value.trim() : DEFAULT_USER_PREFERENCES.messagesThreadFilterAssigneeId;
}

function normalizeMessagesThreadListView(value) {
  const s = typeof value === "string" ? value.trim().toLowerCase() : "";
  return VALID_THREAD_LIST_VIEWS.includes(s) ? s : DEFAULT_THREAD_LIST_VIEW;
}

function normalizeMessagesThreadLens(value) {
  const s = typeof value === "string" ? value.trim().toLowerCase() : "";
  return VALID_THREAD_LENSES.includes(s) ? s : DEFAULT_THREAD_LENS;
}

function normalizeMessagesViewMode(_value) {
  // Triage board view is disabled for now — always use the thread list.
  return DEFAULT_MESSAGES_VIEW_MODE;
}

function normalizeTasksViewMode(value) {
  const s = typeof value === "string" ? value.trim().toLowerCase() : "";
  return VALID_TASKS_VIEW_MODES.includes(s) ? s : DEFAULT_TASKS_VIEW_MODE;
}

function normalizeStringIdList(value) {
  if (!Array.isArray(value)) return [];
  const seen = new Set();
  const result = [];
  for (const item of value) {
    const s = typeof item === "string" ? item.trim() : "";
    if (!s || seen.has(s)) continue;
    seen.add(s);
    result.push(s);
  }
  return result;
}

function normalizeMergedWhatsAppView(value) {
  return typeof value === "boolean" ? value : DEFAULT_MERGED_WHATSAPP_VIEW;
}

function normalizeMergedWaGroupsView(value) {
  return typeof value === "boolean" ? value : DEFAULT_MERGED_WA_GROUPS_VIEW;
}

function normalizeSidebarColorMode(value) {
  const s = typeof value === "string" ? value.trim().toLowerCase() : "";
  return VALID_SIDEBAR_COLOR_MODES.includes(s) ? s : DEFAULT_SIDEBAR_COLOR_MODE;
}

function normalizeSidebarCollapsed(value) {
  return typeof value === "boolean" ? value : DEFAULT_SIDEBAR_COLLAPSED;
}

function normalizeFaceUnlockEnabled(value) {
  return typeof value === "boolean" ? value : DEFAULT_FACE_UNLOCK_ENABLED;
}

function normalizeFaceAutoSwitchEnabled(value) {
  return typeof value === "boolean" ? value : DEFAULT_FACE_AUTO_SWITCH_ENABLED;
}

function normalizeFaceLockOnStrangerEnabled(value) {
  return typeof value === "boolean" ? value : DEFAULT_FACE_LOCK_ON_STRANGER_ENABLED;
}

function normalizeFaceStrangerGraceSeconds(value) {
  const num = Math.round(Number(value));
  if (!Number.isFinite(num)) {
    return DEFAULT_FACE_STRANGER_GRACE_SECONDS;
  }
  return Math.min(MAX_FACE_STRANGER_GRACE_SECONDS, Math.max(MIN_FACE_STRANGER_GRACE_SECONDS, num));
}

function normalizeFaceOwnerAbsentSeconds(value) {
  const num = Math.round(Number(value));
  if (!Number.isFinite(num)) {
    return DEFAULT_FACE_OWNER_ABSENT_SECONDS;
  }
  return Math.min(MAX_FACE_OWNER_ABSENT_SECONDS, Math.max(MIN_FACE_OWNER_ABSENT_SECONDS, num));
}

function normalizeFaceCameraDeviceId(value) {
  return typeof value === "string" ? value.trim() : DEFAULT_FACE_CAMERA_DEVICE_ID;
}

// A stored zone must be one the runtime can actually format in — otherwise every toLocale* / luxon
// call using it would throw. Reject anything unparseable (including "") back to the system-default
// sentinel. Kept self-contained (no import of timezone.js) so this leaf store has no cycle.
function normalizeAppTimeZone(value) {
  const zone = typeof value === "string" ? value.trim() : "";
  if (!zone) return DEFAULT_APP_TIME_ZONE;
  try {
    // Throws RangeError for an unknown IANA zone.
    new Intl.DateTimeFormat("en-US", { timeZone: zone });
    return zone;
  } catch {
    return DEFAULT_APP_TIME_ZONE;
  }
}

function normalizeMessagesThreadListCollapsedSections(value) {
  if (!Array.isArray(value)) return [];
  const seen = new Set();
  const result = [];
  for (const item of value) {
    const s = typeof item === "string" ? item.trim() : "";
    if (!s || seen.has(s)) continue;
    seen.add(s);
    result.push(s);
  }
  return result;
}

// "Rewrite with AI" persona id. Deliberately NOT validated against a fixed enum — the persona
// list is backend-dynamic; an unknown stored id simply falls back to the default at use time.
function normalizeDraftReplyAiPersona(value) {
  return typeof value === "string" ? value.trim() : "";
}

// Dashboard Today-card clinic (tenant) id. Not validated against the clinic list — that is
// backend-dynamic; the card treats an id it can't find as "none selected".
function normalizeDashboardClinicId(value) {
  return typeof value === "string" ? value.trim() : "";
}

// "Rewrite with AI" sparse switch-override map: keep only trimmed-key entries whose value is a
// strict boolean.
function normalizeDraftReplyAiOptions(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const result = {};
  for (const [key, entry] of Object.entries(value)) {
    const k = typeof key === "string" ? key.trim() : "";
    if (!k || typeof entry !== "boolean") continue;
    result[k] = entry;
  }
  return result;
}

function stringArraysEqual(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function booleanMapsEqual(a, b) {
  if (a === b) return true;
  if (!a || typeof a !== "object" || !b || typeof b !== "object") return false;
  const aKeys = Object.keys(a);
  if (aKeys.length !== Object.keys(b).length) return false;
  for (const key of aKeys) {
    if (a[key] !== b[key]) return false;
  }
  return true;
}

function normalizeUserPreferences(value) {
  const source = value && typeof value === "object" ? value : {};
  // Time-grid window is whole hours with the end kept strictly after the start;
  // an inverted/equal pair snaps the end to one hour past the start.
  const calendarDayStartHour = normalizeCalendarDayStartHour(source.calendarDayStartHour);
  let calendarDayEndHour = normalizeCalendarDayEndHour(source.calendarDayEndHour);
  if (calendarDayEndHour <= calendarDayStartHour) {
    calendarDayEndHour = Math.min(24, calendarDayStartHour + 1);
  }
  return {
    dashboardClinicId: normalizeDashboardClinicId(source.dashboardClinicId),
    messagesLensMigrationV1: source.messagesLensMigrationV1 === true,
    calendarZoomPercent: normalizeCalendarZoomPercent(source.calendarZoomPercent),
    calendarMonthZoomPercent: normalizeCalendarMonthZoomPercent(source.calendarMonthZoomPercent),
    calendarWidthZoomPercent: normalizeCalendarWidthZoomPercent(source.calendarWidthZoomPercent),
    calendarSlotOpacityPercent: normalizeCalendarSlotOpacityPercent(source.calendarSlotOpacityPercent),
    calendarView: normalizeCalendarView(source.calendarView),
    calendarUse12HourFormat: normalizeCalendarUse12HourFormat(source.calendarUse12HourFormat),
    calendarIncludeSlots: normalizeCalendarIncludeSlots(source.calendarIncludeSlots),
    calendarIncludeNotAvailableSlots: normalizeCalendarIncludeNotAvailableSlots(
      source.calendarIncludeNotAvailableSlots
    ),
    calendarShowCancelled: normalizeCalendarShowCancelled(source.calendarShowCancelled),
    calendarClinicsQuickFilter: normalizeCalendarQuickFilter(source.calendarClinicsQuickFilter),
    calendarDoctorsQuickFilter: normalizeCalendarQuickFilter(source.calendarDoctorsQuickFilter),
    calendarClinicOrder: normalizeStringIdList(source.calendarClinicOrder),
    calendarDoctorOrder: normalizeStringIdList(source.calendarDoctorOrder),
    calendarFilterBadgesExpanded: normalizeCalendarFilterBadgesExpanded(
      source.calendarFilterBadgesExpanded
    ),
    calendarColumnWidthMode: normalizeCalendarColumnWidthMode(source.calendarColumnWidthMode),
    calendarColumnWidthCustomPx: normalizeCalendarColumnWidthCustomPx(
      source.calendarColumnWidthCustomPx
    ),
    calendarDayViewGroupBy: normalizeCalendarDayViewGroupBy(source.calendarDayViewGroupBy),
    calendarDaySplitEnabled: normalizeCalendarDaySplitEnabled(source.calendarDaySplitEnabled),
    calendarDayStartHour,
    calendarDayEndHour,
    calendarTickMinutes: normalizeCalendarTickMinutes(source.calendarTickMinutes),
    calendarShowSessionRail: normalizeCalendarShowSessionRail(source.calendarShowSessionRail),
    calendarRailWidthPx: normalizeCalendarRailWidthPx(source.calendarRailWidthPx),
    calendarRailColorMode: normalizeCalendarRailColorMode(source.calendarRailColorMode),
    calendarRailMarginPx: normalizeCalendarRailMarginPx(source.calendarRailMarginPx),
    calendarHideSunday: normalizeCalendarHideSunday(source.calendarHideSunday),
    workPlanRailWidthPx: normalizeWorkPlanRailWidthPx(source.workPlanRailWidthPx),
    workPlanHiddenClinicIds: normalizeStringIdList(source.workPlanHiddenClinicIds),
    workPlanClinicOrder: normalizeStringIdList(source.workPlanClinicOrder),
    messagesThreadFilterLockedOnly: normalizeMessagesThreadFilterLockedOnly(
      source.messagesThreadFilterLockedOnly
    ),
    messagesThreadFilterStaffHandlesOnly: normalizeMessagesThreadFilterStaffHandlesOnly(
      source.messagesThreadFilterStaffHandlesOnly
    ),
    messagesThreadFilterAwaitingReplyOnly: normalizeMessagesThreadFilterAwaitingReplyOnly(
      source.messagesThreadFilterAwaitingReplyOnly
    ),
    messagesThreadFilterWhatsappOnly: normalizeMessagesThreadFilterWhatsappOnly(
      source.messagesThreadFilterWhatsappOnly
    ),
    messagesThreadFilterAssignedToMe: normalizeMessagesThreadFilterAssignedToMe(
      source.messagesThreadFilterAssignedToMe
    ),
    messagesThreadFilterAssigneeId: normalizeMessagesThreadFilterAssigneeId(
      source.messagesThreadFilterAssigneeId
    ),
    messagesThreadLens: normalizeMessagesThreadLens(source.messagesThreadLens),
    messagesViewMode: normalizeMessagesViewMode(source.messagesViewMode),
    tasksViewMode: normalizeTasksViewMode(source.tasksViewMode),
    tasksBoardVisibleAssigneeIds: normalizeStringIdList(source.tasksBoardVisibleAssigneeIds),
    mutedChatThreadIds: normalizeStringIdList(source.mutedChatThreadIds),
    messagesThreadListView: normalizeMessagesThreadListView(source.messagesThreadListView),
    mergedWhatsAppView: normalizeMergedWhatsAppView(source.mergedWhatsAppView),
    mergedWaGroupsView: normalizeMergedWaGroupsView(source.mergedWaGroupsView),
    messagesThreadListCollapsedSections: normalizeMessagesThreadListCollapsedSections(
      source.messagesThreadListCollapsedSections
    ),
    messagesParticipantsCollapsedSections: normalizeMessagesThreadListCollapsedSections(
      source.messagesParticipantsCollapsedSections
    ),
    draftReplyAiPersona: normalizeDraftReplyAiPersona(source.draftReplyAiPersona),
    draftReplyAiOptions: normalizeDraftReplyAiOptions(source.draftReplyAiOptions),
    sidebarColorMode: normalizeSidebarColorMode(source.sidebarColorMode),
    sidebarCollapsed: normalizeSidebarCollapsed(source.sidebarCollapsed),
    faceUnlockEnabled: normalizeFaceUnlockEnabled(source.faceUnlockEnabled),
    faceAutoSwitchEnabled: normalizeFaceAutoSwitchEnabled(source.faceAutoSwitchEnabled),
    faceLockOnStrangerEnabled: normalizeFaceLockOnStrangerEnabled(source.faceLockOnStrangerEnabled),
    faceStrangerGraceSeconds: normalizeFaceStrangerGraceSeconds(source.faceStrangerGraceSeconds),
    faceOwnerAbsentSeconds: normalizeFaceOwnerAbsentSeconds(source.faceOwnerAbsentSeconds),
    faceCameraDeviceId: normalizeFaceCameraDeviceId(source.faceCameraDeviceId),
    appTimeZone: normalizeAppTimeZone(source.appTimeZone),
  };
}

// NOTE: the pre-account read-time migrations (the legacy healo.messages.threadListFilters
// import, the lens reset, the board-view cleanup) are gone with the localStorage blob they
// migrated: account blobs are only ever written by post-migration code, and the migration
// explicitly starts every account from factory defaults (no seeding).

function readDeviceFaceOverrides() {
  if (!canUseBrowserStorage()) {
    return {};
  }
  try {
    const raw = window.localStorage.getItem(DEVICE_PREFERENCES_STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      return {};
    }
    const overrides = {};
    for (const key of FACE_PREFERENCE_KEYS) {
      if (parsed[key] !== undefined) {
        overrides[key] = parsed[key];
      }
    }
    return overrides;
  } catch {
    return {};
  }
}

function writeDeviceFaceOverrides(preferences) {
  if (!canUseBrowserStorage()) {
    return;
  }
  try {
    const device = {};
    for (const key of FACE_PREFERENCE_KEYS) {
      device[key] = preferences[key];
    }
    window.localStorage.setItem(DEVICE_PREFERENCES_STORAGE_KEY, JSON.stringify(device));
  } catch {
    /* ignore quota / serialization errors */
  }
}

function stripFacePreferences(preferences) {
  const blob = { ...preferences };
  for (const key of FACE_PREFERENCE_KEYS) {
    delete blob[key];
  }
  return blob;
}

// The merge order is load-bearing: defaults first (so a fresh account blob keeps
// messagesLensMigrationV1 true and every absent key its default), then the account blob,
// then the device face keys (which must win even if a hand-edited server blob smuggled
// face keys in). normalizeUserPreferences clamps whatever came back — a synced blob is
// untrusted input from other devices, exactly like localStorage was.
function buildActivePreferences() {
  const accountBlob = readUserSetting(USER_SETTING_KEYS.UI_PREFERENCES);
  return normalizeUserPreferences({
    ...DEFAULT_USER_PREFERENCES,
    ...(accountBlob && typeof accountBlob === "object" ? accountBlob : {}),
    ...readDeviceFaceOverrides(),
  });
}

// Rebuild + notify on every identity swap (login preload applied, profile switch, logout):
// timezone.js re-derives the luxon default zone from this event, and every subscriber
// re-reads the new user's values.
function ensureActivationSubscription() {
  if (activationSubscribed || typeof window === "undefined") {
    return;
  }
  activationSubscribed = true;
  subscribeUserSettingsActivation(() => {
    inMemoryPreferences = null;
    loadedForUid = null;
    emitPreferencesChange();
  });
}

function emitPreferencesChange() {
  if (typeof window === "undefined") {
    return;
  }
  window.dispatchEvent(new Event(USER_PREFERENCES_CHANGE_EVENT));
}

export function getUserPreferences() {
  ensureActivationSubscription();
  const uid = getActiveSettingsUid();
  if (inMemoryPreferences && loadedForUid === uid) {
    return inMemoryPreferences;
  }
  inMemoryPreferences = buildActivePreferences();
  loadedForUid = uid;
  return inMemoryPreferences;
}

export function setUserPreferences(next) {
  const previous = getUserPreferences();
  const normalizedNext = normalizeUserPreferences({
    ...previous,
    ...(next && typeof next === "object" ? next : {}),
  });

  const changed =
    previous.dashboardClinicId !== normalizedNext.dashboardClinicId ||
    previous.sidebarColorMode !== normalizedNext.sidebarColorMode ||
    previous.sidebarCollapsed !== normalizedNext.sidebarCollapsed ||
    previous.faceUnlockEnabled !== normalizedNext.faceUnlockEnabled ||
    previous.faceAutoSwitchEnabled !== normalizedNext.faceAutoSwitchEnabled ||
    previous.faceLockOnStrangerEnabled !== normalizedNext.faceLockOnStrangerEnabled ||
    previous.faceStrangerGraceSeconds !== normalizedNext.faceStrangerGraceSeconds ||
    previous.faceOwnerAbsentSeconds !== normalizedNext.faceOwnerAbsentSeconds ||
    previous.faceCameraDeviceId !== normalizedNext.faceCameraDeviceId ||
    previous.appTimeZone !== normalizedNext.appTimeZone ||
    previous.calendarZoomPercent !== normalizedNext.calendarZoomPercent ||
    previous.calendarMonthZoomPercent !== normalizedNext.calendarMonthZoomPercent ||
    previous.calendarWidthZoomPercent !== normalizedNext.calendarWidthZoomPercent ||
    previous.calendarSlotOpacityPercent !== normalizedNext.calendarSlotOpacityPercent ||
    previous.calendarView !== normalizedNext.calendarView ||
    previous.calendarUse12HourFormat !== normalizedNext.calendarUse12HourFormat ||
    previous.calendarIncludeSlots !== normalizedNext.calendarIncludeSlots ||
    previous.calendarIncludeNotAvailableSlots !== normalizedNext.calendarIncludeNotAvailableSlots ||
    previous.calendarShowCancelled !== normalizedNext.calendarShowCancelled ||
    previous.calendarClinicsQuickFilter !== normalizedNext.calendarClinicsQuickFilter ||
    previous.calendarDoctorsQuickFilter !== normalizedNext.calendarDoctorsQuickFilter ||
    previous.calendarFilterBadgesExpanded !== normalizedNext.calendarFilterBadgesExpanded ||
    previous.calendarColumnWidthMode !== normalizedNext.calendarColumnWidthMode ||
    previous.calendarColumnWidthCustomPx !== normalizedNext.calendarColumnWidthCustomPx ||
    previous.calendarDayViewGroupBy !== normalizedNext.calendarDayViewGroupBy ||
    previous.calendarDaySplitEnabled !== normalizedNext.calendarDaySplitEnabled ||
    previous.calendarDayStartHour !== normalizedNext.calendarDayStartHour ||
    previous.calendarDayEndHour !== normalizedNext.calendarDayEndHour ||
    previous.calendarTickMinutes !== normalizedNext.calendarTickMinutes ||
    previous.calendarShowSessionRail !== normalizedNext.calendarShowSessionRail ||
    previous.calendarRailWidthPx !== normalizedNext.calendarRailWidthPx ||
    previous.calendarRailColorMode !== normalizedNext.calendarRailColorMode ||
    previous.calendarRailMarginPx !== normalizedNext.calendarRailMarginPx ||
    previous.calendarHideSunday !== normalizedNext.calendarHideSunday ||
    previous.workPlanRailWidthPx !== normalizedNext.workPlanRailWidthPx ||
    previous.messagesThreadFilterLockedOnly !== normalizedNext.messagesThreadFilterLockedOnly ||
    previous.messagesThreadFilterStaffHandlesOnly !== normalizedNext.messagesThreadFilterStaffHandlesOnly ||
    previous.messagesThreadFilterAwaitingReplyOnly !== normalizedNext.messagesThreadFilterAwaitingReplyOnly ||
    previous.messagesThreadFilterWhatsappOnly !== normalizedNext.messagesThreadFilterWhatsappOnly ||
    previous.messagesThreadFilterAssignedToMe !== normalizedNext.messagesThreadFilterAssignedToMe ||
    previous.messagesThreadFilterAssigneeId !== normalizedNext.messagesThreadFilterAssigneeId ||
    previous.messagesThreadLens !== normalizedNext.messagesThreadLens ||
    previous.messagesViewMode !== normalizedNext.messagesViewMode ||
    previous.tasksViewMode !== normalizedNext.tasksViewMode ||
    previous.messagesThreadListView !== normalizedNext.messagesThreadListView ||
    previous.mergedWhatsAppView !== normalizedNext.mergedWhatsAppView ||
    previous.mergedWaGroupsView !== normalizedNext.mergedWaGroupsView ||
    previous.draftReplyAiPersona !== normalizedNext.draftReplyAiPersona ||
    !booleanMapsEqual(previous.draftReplyAiOptions, normalizedNext.draftReplyAiOptions) ||
    !stringArraysEqual(
      previous.messagesThreadListCollapsedSections,
      normalizedNext.messagesThreadListCollapsedSections,
    ) ||
    !stringArraysEqual(
      previous.messagesParticipantsCollapsedSections,
      normalizedNext.messagesParticipantsCollapsedSections,
    ) ||
    !stringArraysEqual(
      previous.tasksBoardVisibleAssigneeIds,
      normalizedNext.tasksBoardVisibleAssigneeIds,
    ) ||
    !stringArraysEqual(previous.mutedChatThreadIds, normalizedNext.mutedChatThreadIds) ||
    !stringArraysEqual(
      previous.workPlanHiddenClinicIds,
      normalizedNext.workPlanHiddenClinicIds,
    ) ||
    !stringArraysEqual(previous.workPlanClinicOrder, normalizedNext.workPlanClinicOrder) ||
    !stringArraysEqual(previous.calendarClinicOrder, normalizedNext.calendarClinicOrder) ||
    !stringArraysEqual(previous.calendarDoctorOrder, normalizedNext.calendarDoctorOrder);

  if (!changed) {
    return previous;
  }

  inMemoryPreferences = normalizedNext;
  loadedForUid = getActiveSettingsUid();

  // Route each half to its store: face keys always land on the device (they must work
  // pre-auth), the rest goes to the account blob — a no-op while logged out, where the
  // only reachable setters are the face-unlock ones anyway.
  writeDeviceFaceOverrides(normalizedNext);
  if (loadedForUid) {
    writeUserSetting(USER_SETTING_KEYS.UI_PREFERENCES, stripFacePreferences(normalizedNext));
  }

  emitPreferencesChange();
  return inMemoryPreferences;
}

export function subscribeUserPreferences(listener) {
  if (typeof window === "undefined" || typeof listener !== "function") {
    return () => {};
  }
  const handler = () => listener(getUserPreferences());
  window.addEventListener(USER_PREFERENCES_CHANGE_EVENT, handler);
  return () => {
    window.removeEventListener(USER_PREFERENCES_CHANGE_EVENT, handler);
  };
}

/** Muted internal chats as a fresh Set (account-bound, like the other preferences here). */
export function getMutedChatThreadIds() {
  return new Set(getUserPreferences().mutedChatThreadIds || []);
}

/** Whether one internal chat is muted (no notifications; excluded from the nav bubble). */
export function isChatMuted(threadId) {
  const tid = String(threadId || "").trim();
  return Boolean(tid) && (getUserPreferences().mutedChatThreadIds || []).includes(tid);
}

/** Flips one chat's mute and persists; returns the next muted-id Set. */
export function toggleChatMuted(threadId) {
  const tid = String(threadId || "").trim();
  if (!tid) return getMutedChatThreadIds();
  const current = getUserPreferences().mutedChatThreadIds || [];
  const next = current.includes(tid) ? current.filter((x) => x !== tid) : [...current, tid];
  setUserPreferences({ mutedChatThreadIds: next });
  return new Set(next);
}
